'use client'

import { Pause, Play, RotateCcw, Volume2, VolumeX, X } from 'lucide-react'
import React, { FC, useRef } from 'react'
import { useFloatingPiPControls } from './useFloatingPiPControls'

export interface FloatingPiPProps {
  videoUrl: string | null
  currentTime: number
  isOpen: boolean
  onClose: () => void
  onReopen?: (currentTime: number) => void
  isMuted?: boolean
}

// Vertical PiP for portrait/short videos (9:16 ratio)
const PIP_WIDTH = 203
const PIP_HEIGHT = 360

const FloatingPiP: FC<FloatingPiPProps> = ({ videoUrl, currentTime, isOpen, onClose, onReopen, isMuted = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null)

  const {
    isClosing,
    isPaused,
    isHovered,
    muted,
    isEnded,
    setIsHovered,
    handleClose,
    handleVideoEnd,
    handleTogglePlayPause,
    handleToggleMute,
    handleReload,
    handlePiPClick,
  } = useFloatingPiPControls({
    videoRef,
    isOpen,
    videoUrl,
    currentTime,
    isMuted,
    onClose,
    onReopen,
  })

  if (!isOpen || !videoUrl) return null

  return (
    <>
      <style>{`
        .floating-pip {
          position: fixed;
          bottom: 24px;
          right: 24px;
          width: ${PIP_WIDTH}px;
          height: ${PIP_HEIGHT}px;
          background: #000;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
          z-index: 99999;
          animation: pipSlideIn 300ms ease-out;
          pointer-events: auto;
          cursor: pointer;
        }

        .floating-pip.closing {
          animation: pipSlideOut 300ms ease-out forwards;
        }

        @keyframes pipSlideIn {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes pipSlideOut {
          from {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          to {
            opacity: 0;
            transform: translateY(20px) scale(0.9);
          }
        }

        .floating-pip-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .floating-pip-close {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.6);
          border: none;
          border-radius: 50%;
          cursor: pointer;
          color: white;
          transition: all 200ms ease;
          z-index: 10;
        }

        .floating-pip-close:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: scale(1.1);
        }

        .floating-pip-mute {
          position: absolute;
          bottom: 8px;
          left: 8px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.6);
          border: none;
          border-radius: 50%;
          cursor: pointer;
          color: white;
          transition: all 200ms ease;
          z-index: 10;
          opacity: 0;
          pointer-events: none;
        }

        .floating-pip-mute.visible {
          opacity: 1;
          pointer-events: auto;
        }

        .floating-pip-mute:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: scale(1.1);
        }

        .floating-pip-hover-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.35);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 200ms ease;
          pointer-events: none;
        }

        .floating-pip-hover-overlay.visible {
          opacity: 1;
          pointer-events: auto;
        }

        .floating-pip-end-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 1;
          pointer-events: auto;
        }

        .floating-pip-play-pause {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.7);
          border: 2px solid rgba(255, 255, 255, 0.8);
          border-radius: 50%;
          cursor: pointer;
          color: white;
          transition: all 200ms ease;
        }

        .floating-pip-play-pause:hover {
          background: rgba(0, 0, 0, 0.85);
          transform: scale(1.1);
        }

        .floating-pip-controls {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 8px;
          background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
        }

        .floating-pip-progress {
          width: 100%;
          height: 3px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 2px;
          overflow: hidden;
        }

        .floating-pip-progress-bar {
          height: 100%;
          background: var(--video-brand-color, var(--video-accent-color, #2196F3));
          transition: width 100ms linear;
        }
      `}</style>

      <div
        className={`floating-pip ${isClosing ? 'closing' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handlePiPClick}
      >
        <video
          ref={videoRef}
          className="floating-pip-video"
          src={videoUrl}
          autoPlay
          playsInline
          muted={muted}
          onEnded={handleVideoEnd}
        />

        <button type="button" className="floating-pip-close" onClick={handleClose} aria-label="Close">
          <X size={16} />
        </button>

        {/* Mute button - visible on hover, hidden when ended */}
        <button
          type="button"
          className={`floating-pip-mute ${isHovered && !isEnded ? 'visible' : ''}`}
          onClick={handleToggleMute}
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
        </button>

        {/* End overlay - always visible when video has ended */}
        {isEnded && (
          <div className="floating-pip-end-overlay">
            <button
              type="button"
              className="floating-pip-play-pause"
              onClick={handleReload}
              aria-label="Replay"
            >
              <RotateCcw size={22} />
            </button>
          </div>
        )}

        {/* Hover overlay with play/pause button - only during normal playback */}
        {!isEnded && (
          <div className={`floating-pip-hover-overlay ${isHovered ? 'visible' : ''}`}>
            <button
              type="button"
              className="floating-pip-play-pause"
              onClick={handleTogglePlayPause}
              aria-label={isPaused ? 'Play' : 'Pause'}
            >
              {isPaused ? <Play size={22} /> : <Pause size={22} />}
            </button>
          </div>
        )}
      </div>
    </>
  )
}

export default FloatingPiP
